import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Sparkles, Eye, MapPin } from 'lucide-react';
import InfoTooltip from '@/components/InfoTooltip';
import { pickLang, RTL_LANGS, COMMON, tt } from '@/utils/localizedStrings';
import { tooltipText, visibilityLabel, NMC_LABELS } from '@/utils/tooltipStrings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getLocale = (lang) => {
  if (lang === 'ar') return 'ar-SA-u-ca-gregory-nu-latn';
  if (lang === 'ur') return 'ur-PK-u-ca-gregory-nu-latn';
  if (lang === 'id') return 'id-ID';
  if (lang === 'fr') return 'fr-FR';
  if (lang === 'bn') return 'bn-BD-u-nu-latn';
  if (lang === 'zh') return 'zh-CN';
  if (lang === 'fa') return 'fa-IR-u-ca-gregory-nu-latn';
  if (lang === 'tr') return 'tr-TR';
  return 'en-US';
};

const NewMoonCountdown = ({ surfaceColors, textColors, location, timezone, cityLabel }) => {
  const { t, i18n } = useTranslation();
  const lang = pickLang(i18n.language);
  const isRTL = RTL_LANGS.has(lang);
  const isAR = lang === 'ar';  // for content selection (vs isRTL for layout)
  const [newMoonData, setNewMoonData] = useState(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Re-fetch whenever location or timezone changes (city-specific)
  useEffect(() => {
    const fetchNewMoon = async () => {
      try {
        const params = {};
        if (location?.lat != null && location?.lon != null) {
          params.lat = location.lat;
          params.lon = location.lon;
        }
        if (timezone) params.tz = timezone;
        const response = await axios.get(`${API}/next-new-moon`, { params });
        setNewMoonData(response.data);
      } catch (err) {
        console.error('Error fetching new moon:', err);
      }
    };
    fetchNewMoon();
    const interval = setInterval(fetchNewMoon, 3600000);
    return () => clearInterval(interval);
  }, [location?.lat, location?.lon, timezone]);

  useEffect(() => {
    if (!newMoonData) return;

    const updateCountdown = () => {
      const now = new Date();
      const target = new Date(newMoonData.next_new_moon_utc);
      const diff = target - now;

      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setCountdown({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [newMoonData]);

  if (!newMoonData) {
    return (
      <div
        className="p-6 md:p-8 rounded-2xl backdrop-blur-2xl border glass-surface"
        style={{ background: surfaceColors.background, borderColor: surfaceColors.border }}
        data-testid="new-moon-countdown"
      >
        <div style={{ color: textColors.secondary }}>{t('loading')}</div>
      </div>
    );
  }

  const monthName = isAR ? newMoonData.hijri_month_name_ar : newMoonData.hijri_month_name_en;
  const locale = getLocale(i18n.language);

  const formatConjunctionLocal = () => {
    const iso = newMoonData.local_conjunction_time || newMoonData.next_new_moon_utc;
    const d = new Date(iso);
    return d.toLocaleString(locale, {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const formatVisibilityDate = () => {
    if (!newMoonData.first_visibility_date_local) return null;
    const d = new Date(newMoonData.first_visibility_date_local + 'T12:00:00');
    return d.toLocaleDateString(locale, {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  // Format the "After sunset of {date} at HH:MM" sub-line under the first-visibility
  // date. Returned as null if the backend didn't provide a sunset time.
  const formatSunsetLine = () => {
    if (!newMoonData.first_visibility_date_local || !newMoonData.sunset_time_local) return null;
    const [hh, mm] = newMoonData.sunset_time_local.split(':');
    // Build a Date whose date-part matches the local visibility date and whose
    // hh:mm are the local sunset time. toLocaleTimeString will honour the user
    // locale (12h/24h + numerals) without any timezone conversion.
    const [yy, mo, dd] = newMoonData.first_visibility_date_local.split('-');
    const d = new Date(
      Number(yy), Number(mo) - 1, Number(dd),
      Number(hh), Number(mm), 0,
    );
    const timeLabel = d.toLocaleTimeString(locale, {
      hour: '2-digit', minute: '2-digit'
    });
    const prefix = tt(NMC_LABELS.afterSunsetPrefix, lang);
    return `${prefix} · ${timeLabel}`;
  };

  const visNote = isRTL
    ? newMoonData.visibility_note_ar
    : newMoonData.visibility_note_en;

  // Visibility color
  const visColor = {
    easy: '#34D399',
    possible: '#FBBF24',
    difficult: '#FB923C',
    not_visible: '#94A3B8'
  }[newMoonData.visibility_status] || '#94A3B8';

  return (
    <div
      className="p-6 md:p-8 rounded-2xl backdrop-blur-2xl border glass-surface"
      style={{ background: surfaceColors.background, borderColor: surfaceColors.border }}
      data-testid="new-moon-countdown"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Header — three lines:
          Line 1: icon alone, aligned to the start of the reading direction
          Line 2: preface text (centered), single language of the current UI
          Line 3: target Hijri month name + year (centered, highlighted). */}
      <div className="mb-5">
        <div
          className="flex mb-2 justify-start"
        >
          <Sparkles size={22} style={{ color: textColors.accent }} />
        </div>
        <div className="text-center mb-2">
          <h3
            className="text-heading text-base md:text-lg"
            style={{ color: textColors.primary }}
            data-testid="new-moon-title"
          >
            {t('nextNewMoonPreface')}
          </h3>
        </div>
        <div className="text-center">
          <span
            style={{
              color: textColors.accent,
              fontWeight: 800,
              fontSize: '1.15rem',
              letterSpacing: '0.3px',
              display: 'inline-block',
              borderBottom: `1px solid ${textColors.accent}55`,
              paddingBottom: '2px',
            }}
            data-testid="new-moon-month-name"
          >
            {/* In Arabic we insert "لعام" between month and year for a
                slightly more literary phrasing (per user request). Other
                languages use the natural "MonthName Year" pattern. */}
            {isAR
              ? `${monthName} لعام ${newMoonData.hijri_year}`
              : `${monthName} ${newMoonData.hijri_year}`}
          </span>
        </div>
      </div>

      {/* "After" label */}
      <div className="mb-3 flex" style={{ justifyContent: isRTL ? 'flex-end' : 'flex-start' }}>
        <div
          className="inline-flex items-center px-4 py-1.5 rounded-full"
          style={{
            background: `${textColors.accent}22`,
            border: `1px solid ${textColors.accent}66`,
            color: textColors.accent
          }}
          data-testid="countdown-prefix"
        >
          <span className="text-base md:text-lg font-semibold tracking-wide">{t('after')}</span>
        </div>
      </div>

      {/* Countdown Display */}
      <div className="grid grid-cols-4 gap-2 md:gap-3 mb-5" data-testid="countdown-display" dir="ltr">
        {[
          { value: countdown.days, labelKey: 'day', testId: 'countdown-days' },
          { value: countdown.hours, labelKey: 'hours', testId: 'countdown-hours' },
          { value: countdown.minutes, labelKey: null, label: tt(COMMON.min, lang), testId: 'countdown-minutes' },
          { value: countdown.seconds, labelKey: null, label: tt(COMMON.sec, lang), testId: 'countdown-seconds' }
        ].map((item, i) => (
          <div
            key={i}
            className="text-center p-3 lg:p-4 rounded-xl"
            style={{ background: 'rgba(255, 255, 255, 0.08)' }}
            data-testid={item.testId}
          >
            <div
              className="text-display tabular-nums"
              style={{
                color: textColors.primary,
                fontVariantNumeric: 'tabular-nums',
                fontSize: 'clamp(1.5rem, 2.4vw, 2.6rem)',
                lineHeight: 1
              }}
            >
              {String(item.value).padStart(2, '0')}
            </div>
            <div
              className="text-caption mt-1 lg:mt-2"
              style={{
                color: textColors.secondary,
                fontSize: 'clamp(0.625rem, 0.85vw, 0.95rem)'
              }}
            >
              {item.labelKey ? t(item.labelKey) : item.label}
            </div>
          </div>
        ))}
      </div>

      {/* Local conjunction time */}
      <div
        className="text-xs mono-text pt-4 border-t"
        style={{ borderColor: surfaceColors.border, color: textColors.secondary }}
        data-testid="new-moon-precise-time"
      >
        <div className="flex items-center gap-1.5 text-caption mb-1.5">
          {cityLabel && <MapPin size={11} style={{ flexShrink: 0 }} />}
          <span>
            {tt(NMC_LABELS.localConjunction, lang)}
            {cityLabel ? ` — ${cityLabel}` : ''}
          </span>
          <InfoTooltip
            testId="tooltip-conjunction"
            text={tooltipText('conjunction', lang)}
          />
        </div>
        <div className="text-body" style={{ color: textColors.primary }} data-testid="conjunction-local-time">
          {formatConjunctionLocal()}
        </div>
      </div>

      {/* LOCAL CRESCENT VISIBILITY - city-specific */}
      {newMoonData.first_visibility_date_local && (
        <div
          className="mt-4 p-4 rounded-xl"
          style={{
            background: `${visColor}1A`,
            border: `1px solid ${visColor}55`
          }}
          data-testid="local-crescent-visibility"
        >
          <div className="flex items-start gap-2 mb-2">
            <Eye size={16} style={{ color: visColor, marginTop: '2px', flexShrink: 0 }} />
            <div className="flex-1">
              <div
                className="text-xs text-caption mb-0.5 flex items-center gap-1.5"
                style={{ color: textColors.secondary }}
              >
                <span>
                  {tt(NMC_LABELS.firstCrescent, lang)}
                  {cityLabel ? ` — ${cityLabel}` : ''}
                </span>
                <InfoTooltip
                  testId="tooltip-first-visibility"
                  text={tooltipText('firstVisibility', lang)}
                />
              </div>
              <div
                className="text-sm font-semibold"
                style={{ color: textColors.primary }}
                data-testid="first-visibility-date"
              >
                {formatVisibilityDate()}
              </div>
              {formatSunsetLine() && (
                <div
                  className="text-xs mt-0.5 tabular-nums"
                  style={{ color: textColors.secondary }}
                  data-testid="first-visibility-sunset"
                >
                  {formatSunsetLine()}
                </div>
              )}
            </div>
            <div
              className="text-xs px-2 py-1 rounded-full font-semibold flex items-center gap-1"
              style={{ background: `${visColor}33`, color: visColor }}
              data-testid="visibility-status"
            >
              <span>
                {visibilityLabel(newMoonData.visibility_status, lang)}
              </span>
              <InfoTooltip
                testId="tooltip-visibility-status"
                size={12}
                dim={false}
                text={tooltipText('visibilityStatus', lang)}
              />
            </div>
          </div>
          {visNote && (
            <div className="text-xs text-body mt-2" style={{ color: textColors.primary }}>
              {visNote}
            </div>
          )}
          {/* Technical details */}
          <div
            className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t"
            style={{ borderColor: `${visColor}33`, color: textColors.secondary }}
          >
            <div className="flex flex-col items-center text-center" data-testid="metric-moon-age">
              <div className="flex items-center justify-center gap-1 mb-1" dir={isRTL ? 'rtl' : 'ltr'}>
                <span style={{ fontSize: 'clamp(0.625rem, 0.85vw, 0.8rem)', lineHeight: 1.3 }}>
                  {tt(NMC_LABELS.crescentAge, lang)}
                </span>
                <InfoTooltip
                  testId="tooltip-moon-age"
                  text={tooltipText('moonAge', lang)}
                />
              </div>
              <div
                className="tabular-nums mono-text"
                style={{
                  color: textColors.primary,
                  fontWeight: 700,
                  fontSize: 'clamp(1.05rem, 1.7vw, 1.6rem)',
                  lineHeight: 1.1
                }}
                dir="ltr"
              >
                {newMoonData.moon_age_at_sunset_hours}<span style={{ fontSize: '0.65em', opacity: 0.75, marginInlineStart: '0.15em' }}>h</span>
              </div>
            </div>

            <div className="flex flex-col items-center text-center" data-testid="metric-altitude">
              <div className="flex items-center justify-center gap-1 mb-1" dir={isRTL ? 'rtl' : 'ltr'}>
                <span style={{ fontSize: 'clamp(0.625rem, 0.85vw, 0.8rem)', lineHeight: 1.3 }}>
                  {tt(NMC_LABELS.moonAltitude, lang)}
                </span>
                <InfoTooltip
                  testId="tooltip-altitude"
                  text={tooltipText('altitudeMoon', lang)}
                />
              </div>
              <div
                className="tabular-nums mono-text"
                style={{
                  color: textColors.primary,
                  fontWeight: 700,
                  fontSize: 'clamp(1.05rem, 1.7vw, 1.6rem)',
                  lineHeight: 1.1
                }}
                dir="ltr"
              >
                {newMoonData.moon_altitude_at_sunset}<span style={{ fontSize: '0.7em', opacity: 0.85, marginInlineStart: '0.05em' }}>°</span>
              </div>
            </div>

            <div className="flex flex-col items-center text-center" data-testid="metric-elongation">
              <div className="flex items-center justify-center gap-1 mb-1" dir={isRTL ? 'rtl' : 'ltr'}>
                <span style={{ fontSize: 'clamp(0.625rem, 0.85vw, 0.8rem)', lineHeight: 1.3 }}>
                  {tt(NMC_LABELS.sunMoonElongation, lang)}
                </span>
                <InfoTooltip
                  testId="tooltip-elongation"
                  text={tooltipText('elongation', lang)}
                />
              </div>
              <div
                className="tabular-nums mono-text"
                style={{
                  color: textColors.primary,
                  fontWeight: 700,
                  fontSize: 'clamp(1.05rem, 1.7vw, 1.6rem)',
                  lineHeight: 1.1
                }}
                dir="ltr"
              >
                {newMoonData.moon_sun_elongation}<span style={{ fontSize: '0.7em', opacity: 0.85, marginInlineStart: '0.05em' }}>°</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {newMoonData.visibility_status === 'not_visible' && newMoonData.first_visibility_date_local == null && (
        <div
          className="mt-4 p-3 rounded-lg flex items-start gap-2"
          style={{ background: 'rgba(148, 163, 184, 0.12)', border: '1px solid rgba(148, 163, 184, 0.3)' }}
        >
          <Eye size={14} style={{ color: '#94A3B8', marginTop: '2px', flexShrink: 0 }} />
          <div className="text-xs" style={{ color: textColors.secondary }}>
            {isAR ? newMoonData.visibility_note_ar : newMoonData.visibility_note_en}
          </div>
        </div>
      )}

      {/* Umm Al-Qura civil-calendar disclosure — shown at the very bottom of the
          card as a subtle, italic-toned paragraph. Educates the user on why the
          Falak Hijri day-1 may fall one day earlier than sighting-based countries. */}
      <div
        className="mt-5 pt-4 border-t text-[11px] leading-relaxed"
        style={{ borderColor: surfaceColors.border, color: textColors.secondary, opacity: 0.85 }}
        data-testid="umm-al-qura-note"
      >
        {tt(NMC_LABELS.ummAlQuraNote, lang)}
      </div>
    </div>
  );
};

export default NewMoonCountdown;
